import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ConfigurationService from "../pages/Configuration/configuration.service";

export const SENSITIVE_UNLOCK_STORAGE_KEY = "vsara_sensitive_unlocked_pages";

const SensitiveInfoContext = createContext({
  isProtected: false,
  isUnlocked: true,
  requestUnlock: async () => true,
  lockPage: () => {},
  refreshPasswordFlag: async () => {},
});

function loadUnlockedPages() {
  try {
    const raw = sessionStorage.getItem(SENSITIVE_UNLOCK_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (e) {
    return new Set();
  }
}

function persistUnlockedPages(setValue) {
  sessionStorage.setItem(SENSITIVE_UNLOCK_STORAGE_KEY, JSON.stringify(Array.from(setValue)));
}

export function clearSensitiveUnlockSession() {
  sessionStorage.removeItem(SENSITIVE_UNLOCK_STORAGE_KEY);
}

export function SensitiveInfoProvider({ children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const pageKey = location.pathname;
  const [passwordSet, setPasswordSet] = useState(false);
  const [unlockedPages, setUnlockedPages] = useState(loadUnlockedPages);
  const [modalOpen, setModalOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const waitersRef = useRef([]);

  const refreshPasswordFlag = useCallback(async () => {
    const sessionID = localStorage.getItem("session-id");
    if (!sessionID) {
      setPasswordSet(false);
      return;
    }
    try {
      const response = await ConfigurationService.getConfiguration(sessionID);
      const rec = response?.data?.data?.[0];
      setPasswordSet(Boolean(rec?.sensitive_info_password_set));
    } catch (e) {
      setPasswordSet(false);
    }
  }, []);

  useEffect(() => {
    refreshPasswordFlag();
  }, [refreshPasswordFlag, location.pathname]);

  const isProtected = passwordSet;
  const isUnlocked = !isProtected || unlockedPages.has(pageKey);

  const closeModal = useCallback((result) => {
    waitersRef.current.forEach((resolve) => resolve(result));
    waitersRef.current = [];
    setModalOpen(false);
    setPassword("");
    setFormError("");
    setSubmitting(false);
  }, []);

  const requestUnlock = useCallback(() => {
    if (!passwordSet) return Promise.resolve(true);
    if (unlockedPages.has(pageKey)) return Promise.resolve(true);
    return new Promise((resolve) => {
      waitersRef.current.push(resolve);
      setFormError("");
      setModalOpen(true);
    });
  }, [passwordSet, unlockedPages, pageKey]);

  const lockPage = useCallback(() => {
    setUnlockedPages((prev) => {
      const next = new Set(prev);
      next.delete(pageKey);
      persistUnlockedPages(next);
      return next;
    });
  }, [pageKey]);

  const handleVerify = async (event) => {
    if (event) event.preventDefault();
    if (!password.trim()) {
      setFormError(t("Please enter the password."));
      return;
    }
    setFormError("");
    setSubmitting(true);
    try {
      const sessionID = localStorage.getItem("session-id");
      const response = await ConfigurationService.verifySensitiveInfoPassword(sessionID, password.trim());
      if (response?.data?.message === "Unauthorized") {
        closeModal(false);
        return;
      }
      if (response?.data?.status) {
        setUnlockedPages((prev) => {
          const next = new Set(prev);
          next.add(pageKey);
          persistUnlockedPages(next);
          return next;
        });
        closeModal(true);
      } else {
        setSubmitting(false);
        setFormError(response?.data?.message || t("Incorrect password"));
      }
    } catch (error) {
      setSubmitting(false);
      setFormError(t("Unable to verify password. Please try again."));
    }
  };

  const value = useMemo(
    () => ({
      isProtected,
      isUnlocked,
      requestUnlock,
      lockPage,
      refreshPasswordFlag,
    }),
    [isProtected, isUnlocked, requestUnlock, lockPage, refreshPasswordFlag]
  );

  useEffect(() => {
    if (!modalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.classList.add("vsara-sensitive-unlock-open");
    document.body.style.overflow = "hidden";
    return () => {
      document.body.classList.remove("vsara-sensitive-unlock-open");
      document.body.style.overflow = previousOverflow;
    };
  }, [modalOpen]);

  const unlockModal = modalOpen
    ? ReactDOM.createPortal(
      <div
        className="vsara-sensitive-unlock-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 100000,
          background: "rgba(0, 0, 0, 0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
        onClick={() => closeModal(false)}
      >
        <style>
          {`
            body.vsara-sensitive-unlock-open .modal {
              visibility: hidden !important;
            }
          `}
        </style>
        <div
          className="rounded shadow-lg"
          style={{
            width: "100%",
            maxWidth: 460,
            background: "#ffffff",
            color: "#212529",
            zIndex: 100001,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <form onSubmit={handleVerify}>
            <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
              <h5 className="mb-0">{t("Unlock sensitive information")}</h5>
              <button type="button" className="btn-close" onClick={() => closeModal(false)} />
            </div>
            <div className="px-4 py-3">
              {formError ? (
                <div className="alert alert-danger py-2 mb-3" role="alert">
                  {formError}
                </div>
              ) : null}
              <p className="text-muted mb-3">
                {t("Enter the configuration password to view NRIC, IC images, face photos and medical documents on this page.")}
              </p>
              <label className="fw-semibold form-label">{t("Password")}</label>
              <input
                type="password"
                className={`form-control ${formError ? "is-invalid" : ""}`}
                autoFocus
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (formError) setFormError("");
                }}
                placeholder={t("Enter password")}
                autoComplete="off"
              />
            </div>
            <div className="d-flex justify-content-end gap-2 px-4 py-3 border-top">
              <button type="button" className="btn btn-secondary" onClick={() => closeModal(false)} disabled={submitting}>
                {t("Cancel")}
              </button>
              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? t("Verifying...") : t("Unlock")}
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <SensitiveInfoContext.Provider value={value}>
      {children}
      {unlockModal}
    </SensitiveInfoContext.Provider>
  );
}

export function useSensitiveInfo() {
  return useContext(SensitiveInfoContext);
}

export function useSensitiveAction() {
  const { isProtected, isUnlocked, requestUnlock } = useSensitiveInfo();
  return useCallback(
    async (action) => {
      if (!isProtected || isUnlocked) {
        if (action) action();
        return true;
      }
      const ok = await requestUnlock();
      if (ok && action) action();
      return ok;
    },
    [isProtected, isUnlocked, requestUnlock]
  );
}

export function SensitivePageBanner() {
  const { t } = useTranslation();
  const { isProtected, isUnlocked, lockPage, requestUnlock } = useSensitiveInfo();
  if (!isProtected) return null;
  return (
    <div
      className="d-flex align-items-center justify-content-between mb-3 px-3 py-2 rounded border"
      style={{ backgroundColor: isUnlocked ? "#e8f7ef" : "#f8f9fa" }}
    >
      <div className="small mb-0">
        <i className={`pe-7s-lock me-2 ${isUnlocked ? "text-success" : "text-muted"}`} />
        {isUnlocked
          ? t("Sensitive information is visible on this page.")
          : t("Sensitive information is hidden. Click a locked field to view.")}
      </div>
      {isUnlocked ? (
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={lockPage}>
          {t("Hide")}
        </button>
      ) : (
        <button type="button" className="btn btn-sm btn-success" onClick={requestUnlock}>
          {t("Unlock")}
        </button>
      )}
    </div>
  );
}

export function SensitiveText({ value, emptyLabel = "-" }) {
  const { t } = useTranslation();
  const { isProtected, isUnlocked, requestUnlock } = useSensitiveInfo();
  if (!value) return <span>{emptyLabel}</span>;
  if (!isProtected || isUnlocked) return <span>{value}</span>;
  return (
    <button
      type="button"
      className="btn btn-link p-0 text-decoration-none align-baseline"
      onClick={requestUnlock}
      title={t("Click to enter password and view")}
    >
      <span className="text-muted">••••••••</span>
      <i className="pe-7s-lock ms-1 text-muted" />
    </button>
  );
}

export function SensitiveImage({
  src,
  alt,
  style,
  className,
  onMouseEnter,
  onMouseLeave,
  onClick,
  placeholderStyle,
}) {
  const { t } = useTranslation();
  const { isProtected, isUnlocked, requestUnlock } = useSensitiveInfo();
  if (!src) return <span>{t("No Image")}</span>;
  if (!isProtected || isUnlocked) {
    return (
      <img
        src={src}
        alt={alt}
        style={style}
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={requestUnlock}
      title={t("Click to enter password and view")}
      className="d-inline-flex align-items-center justify-content-center border-0"
      style={{
        width: (placeholderStyle && placeholderStyle.width) || (style && style.width) || 50,
        height: (placeholderStyle && placeholderStyle.height) || (style && style.height) || 50,
        borderRadius: (style && style.borderRadius) || 6,
        background: "#e9ecef",
        color: "#6c757d",
        cursor: "pointer",
        ...placeholderStyle,
      }}
    >
      <i className="pe-7s-lock" />
    </button>
  );
}

export default SensitiveInfoContext;
